import { compact, filter, get, includes, isEqual, last, merge, set, isEmpty } from 'lodash';
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter';
import { MenuModule } from '@ag-grid-enterprise/menu';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { ForgeIcon } from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import uuid from 'uuid';
import DOMPurify from 'dompurify';

import { TableColumnFormat } from './types';
import { AgGridProps } from './types/AgGridProps';
import { PdfPreviewModal } from 'common/components/PdfPreviewModal';
import { ReportMetadata } from 'common/components/PdfPreviewModal/types';
import { TIME_FORMATS } from 'common/DataTypeFormatter';
import { HierarchyColumnConfig, OrderConfig } from 'common/visualizations/vif';
import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnState,
  GridApi,
  GridReadyEvent,
  FirstDataRenderedEvent,
  SortChangedEvent,
  ColumnVisibleEvent,
  ColumnPivotChangedEvent,
  ColumnPivotModeChangedEvent,
  ColumnRowGroupChangedEvent,
  ColumnValueChangedEvent,
  Column,
  RowClassParams,
  MenuItemDef,
  GetMainMenuItemsParams,
  ModelUpdatedEvent,
  ICellRendererParams,
  ProcessCellForExportParams,
  SideBarDef,
  FilterChangedEvent,
  IsServerSideGroupOpenByDefaultParams,
  GetLocaleTextParams,
  GetContextMenuItemsParams,
  CellFocusedEvent,
  HeaderFocusedEvent,
} from '@ag-grid-community/core';
import { Filters } from 'common/components/FilterBar/types';
import { ViewColumn } from 'common/types/viewColumn';
import { agGridDataFormatter, getAgTableRowStyle } from './helpers/TableColumnFormatting';
import ReactDOM from 'react-dom';
import { useGroupStateRestore } from './customHooks/useGroupStateRestore';
import useColDefs from './customHooks/useColDefs';
import SocrataTooltip from './SocrataTooltip';
import './index.scss';
import {
  agGridFilterToVifFilter,
  getDistinctValuesCountForColumns,
} from 'common/visualizations/helpers/AgGridHelpers';
import { RowStripeStyle } from 'common/types/agGrid/rowStripe';
import NoRowsAgGridOverlay from 'common/components/NoRowsAgGridOverlay';
import { isLargeDesktop } from 'common/visualizations/helpers/MediaQueryHelper';
import {
  getColumnId,
  getRowGroupColumnForSort,
  handleIndentedColumnSort,
  handleRegularColumnSort,
  HEADER_SORT_INDEX,
  isSortMenuOrResizeClick,
  translateSortToHeaderSortCycle,
  updateColumnsSort,
} from './helpers/SortHelpers';
import { useAutoGroupAttributes } from './customHooks/useAutoGroupAttributes';
import { GROUP_COLUMN_PREFIX, HEADER_SORT_CYCLE, PIVOT_SEPARATOR } from './Constants';
import { CustomAgGridContext } from 'common/types/agGrid/context';
import { useDeepCompareEffect } from 'common/visualizations/views/agGridReact/customHooks/useDeepCompareEffect';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import ExportGrid, { IExportGridParams } from './ExportGrid';
import { SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import { getCurrentUser } from 'common/current_user';
import {
  constructPivotGrandTotal,
  isPivotFeatureFlagOn,
  isPivotInHierarchy,
} from 'common/visualizations/helpers/PivotHelpers';
import {
  isCustomSortEnabled,
  isTableAdvancedTotalingEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { createGetSortIcons, renderSortIconTooltip } from './helpers/sortIconHelpers';
import eventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { useRenderIndentedHeader, useRenderUnindentedHeader } from './customHooks/useCustomSortRenders';
import { assignAriaSortToTableHeaders, refreshIndentedHeader } from './helpers/customSortRenders';
import { usePivotModeBugFixHack } from './customHooks/usePivotModeBugFixHack';
import { GrandTotalRow } from 'common/types/agGrid/grandTotal';
import { useGridEscapeKeyHandler } from './customHooks/useGridEscapeKeyHandler';
import isBlank from 'common/js_utils/isBlank';

// It's ok for this license key to be exposed (https://www.ag-grid.com/javascript-data-grid/licensing/#setting-the-license-key)
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-073142}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Tyler_Technologies}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Socrata}_only_for_{38}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Socrata}_need_to_be_licensed___{Socrata}_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{14_July_2026}____[v3]_[01]_MTc4Mzk4MzYwMDAwMA==953dd63b704319b712726e8a48b3898f',
);

const defaultColDef = {
  autoHeaderHeight: false,
  autoHeight: false,
  maxWidth: 512,
  minWidth: 80,
  resizable: true,
  sortable: true,
  tooltipComponent: 'customTooltip',
  wrapHeaderText: false,
  wrapText: false,
};

const GridNotMemoed = (props: AgGridProps) => {
  //#region setup
  const {
    actionColumnDef,
    activeHierarchyId,
    agFilterModel,
    agGridOpenNodeLevel,
    columnFormats,
    columnMetadata,
    datasetName,
    datasetUid,
    datasource,
    defaultColDefOverrides,
    description,
    displayColumnFilters,
    domain,
    filterWhereClause,
    getExportData,
    getGrandTotalRow,
    getUniqueValues,
    handleCustomSortCycle,
    handleIndentedHeaderSortIndex,
    headerFormat,
    hierarchyConfig,
    isIndented,
    isPrintModalOpen,
    linkedAction,
    newTableActions,
    nonStandardAggregations,
    onColumnPivotChange,
    onColumnPivotModeChange,
    onColumnReorder,
    onColumnResize,
    onColumnRowGroupChange,
    onColumnSort,
    onColumnValueChange,
    onColumnVisibilityChange,
    onFilterChange,
    onPrintModalClose,
    openToolPanelByDefault,
    paginationPageSize,
    processDiscoveryContext,
    rowStripeStyle,
    searchString,
    showAgGridColumnMenu,
    showColumnMenuKebab,
    title,
    useSetFilters,
    vifColumns,
    vifFilters,
    vifOrderConfig,
    vifParameterOverrides,
    vizUid,
  } = props;

  const { showGrandTotal, showNetTotal, id: hierarchyId } = hierarchyConfig;
  /** Is the agGrid PivotMode UI toggle enabled for this table? */
  const pivotMode = !!hierarchyConfig.pivotMode && isPivotFeatureFlagOn();
  const showAgGridColumnAggregations = props.showAgGridColumnAggregations ?? true;

  const agGridContext = useRef<CustomAgGridContext>({} as CustomAgGridContext);

  const [filters, setFilters] = useState<Filters>([]);
  const [parameterOverrides, setParameterOverrides] = useState<ClientContextVariable[]>([]);
  const [columns, setColumns] = useState<ViewColumn[]>([]);
  const [vifColumnFormat, setVifColumnFormat] = useState<{ [key: string]: TableColumnFormat }>({});
  const [hierarchyColumns, setHierarchyColumns] = useState<HierarchyColumnConfig[]>([]);
  const [removedHierarchyColumnNames, setRemovedHierarchyColumnNames] = useState<Set<string>>(new Set());
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [grandTotalData, setGrandTotalData] = useState<GrandTotalRow>([]);
  const { isServerSideGroupOpenByDefault, onRowGroupOpened } = useGroupStateRestore();
  const [currentRowStripeStyle, setCurrentRowStripeStyle] = useState<RowStripeStyle | undefined>(
    rowStripeStyle,
  );
  const [currentSortState, setCurrentSortState] = useState<OrderConfig[] | []>([]);
  const [sortSourceElement, setSortSourceElement] = useState<string | null>(null);

  const hasAggregation = useMemo(() => {
    if (isTableAdvancedTotalingEnabled()) {
      return !!hierarchyConfig.hasAggregation;
    }
    // INFO: EN-85363 Remove the logic below once advance totaling feature flag is turned on indefinitely
    const aggregateColumns = hierarchyConfig?.columnConfigurations?.filter((col) => !!col.aggregation) ?? [];
    return !!aggregateColumns.length;
  }, [hierarchyConfig?.hasAggregation, hierarchyConfig?.columnConfigurations]);

  const hasRowGroup = useMemo(() => {
    if (isTableAdvancedTotalingEnabled()) {
      const groupingColumnsCount = hierarchyConfig.rowGroupCount;
      return !!groupingColumnsCount;
    }
    // INFO: EN-85363 Remove the logic below once advance totaling feature flag is turned on indefinitely
    const groupingColumns = hierarchyConfig?.columnConfigurations?.filter((col) => col.isGrouping) ?? [];
    return !!groupingColumns.length;
  }, [hierarchyConfig?.rowGroupCount, hierarchyConfig?.columnConfigurations]);

  const showSubTotal = useMemo(() => {
    const showSubtotalConfig = hierarchyConfig?.showSubTotal ?? false;
    return showSubtotalConfig && hasRowGroup && hasAggregation;
  }, [hierarchyConfig?.showSubTotal, hasAggregation, hasRowGroup]);

  const refreshGridData = useCallback(
    (purge?: boolean) => {
      if (gridApi) {
        gridApi.refreshServerSide({ purge });
      }
    },
    [gridApi],
  );

  //#endregion setup

  //#region useLayoutEffect

  // useLayoutEffect runs synchronously after render which helps avoid some ag grid spinning / loading issues
  // don't put anything in here that is async, like data fetching
  useLayoutEffect(() => {
    let shouldRefreshGridData = false;
    let hardRefresh = false;

    if (
      hierarchyConfig.columnConfigurations &&
      !isEqual(hierarchyColumns, hierarchyConfig.columnConfigurations)
    ) {
      const removedColumnNames = hierarchyColumns
        .filter((h) => {
          const vifColumnConfig = hierarchyConfig.columnConfigurations.find(
            (c) => c.columnName === h.columnName,
          );
          const columnRemoved = !vifColumnConfig;
          const columnNoLongerGrouping = h.isGrouping && !vifColumnConfig?.isGrouping;
          return columnRemoved || columnNoLongerGrouping;
        })
        .map(({ columnName }) => columnName);
      setHierarchyColumns(hierarchyConfig.columnConfigurations);
      setRemovedHierarchyColumnNames(new Set(removedColumnNames));
    }

    // the grid itself doesn't use the vif filters at all (they are used during query construction in TableDataHelpers)
    // but we keep a copy of them in state so that we can detect when they change, and refresh the grid.
    if (!isEqual(filters, vifFilters)) {
      if (!fromAddFilter()) {
        shouldRefreshGridData = true;
        // the grid may need a full refresh if the previous filters returned 0 rows
        hardRefresh = true;
      }
      setFilters(vifFilters as Filters);
    }

    // Similar to tracking state of vif filters, we also have a copy of vif parameterOverrides
    // to detect changes and refresh the grid when any values change
    if (!isEqual(parameterOverrides, vifParameterOverrides)) {
      setParameterOverrides(vifParameterOverrides);
      shouldRefreshGridData = true;
      hardRefresh = true;
    }

    if (!isEqual(vifColumnFormat, columnFormats)) {
      setVifColumnFormat(columnFormats);
      // Removing focus from the currently focused cell helps avoid UI issues
      // such as conditional input fields losing focus with every keypress.
      gridApi?.clearFocusedCell();
    }

    if (!isEqual(columns, vifColumns)) {
      // if a new column has been added, tell ag-grid to refresh its data
      if (vifColumns.length > columns.length) {
        shouldRefreshGridData = true;
      }
      // if a column was deleted and a new one took its place, we should also refresh
      const vifColumnNames = vifColumns.map((col) => col.fieldName);
      const columnNames = columns.map((col) => col.fieldName);

      if (vifColumnNames.some((colName) => !columnNames.includes(colName))) {
        // we don't ask ag-grid to refresh in all cases, since closing the open groups can be a disruptive experience
        // and isn't necessary if a column is removed
        shouldRefreshGridData = true;
      }

      setColumns(vifColumns);
    }

    // Update the width of grouped columns for non-indented layout
    if (gridApi) {
      updateGroupedColumnsWidth(gridApi);
    }

    // if a grouped column was sorted on the AX (which is a different grid instance than the story version),
    // the map of vif columns to agColumn defs below will not pick that sort up, because the grouped columns are
    // auto-created by AG Grid and therefore the mapping function never touches them.
    // here we manually compare the vif's view of grouped column sorts with ag-grid's view of grouped column sorts,
    // and if they don't match, we update ag-grid's column state
    if (gridApi && hierarchyConfig.order) {
      const agColumnState = gridApi.getColumnState();
      const agGroupedColumnSortState = agColumnState
        .filter((col) => col.colId && col.sort && col.colId.startsWith(GROUP_COLUMN_PREFIX))
        .map(({ colId, sort, sortIndex }) => ({ colId, sort, sortIndex }));

      const vifGroupedColumnSortState = hierarchyConfig.order
        .map((order, orderIndex) => ({
          sort: order.ascending ? 'asc' : 'desc',
          sortIndex: orderIndex,
          colId: order.columnName,
        }))
        .filter(({ colId }) => colId.startsWith(GROUP_COLUMN_PREFIX));
      if (!isEqual(agGroupedColumnSortState, vifGroupedColumnSortState)) {
        setCurrentSortState(vifOrderConfig);
        updateColumnsSort({ api: gridApi, vifOrderConfig, isIndented });
      }
    }

    if (!isEqual(currentRowStripeStyle, rowStripeStyle)) {
      setCurrentRowStripeStyle(rowStripeStyle);
      shouldRefreshGridData = true;
    }

    if (gridApi && !isEqual(vifOrderConfig, currentSortState)) {
      setCurrentSortState(vifOrderConfig);
      updateColumnsSort({ api: gridApi, vifOrderConfig, isIndented });
    }

    if (gridApi) {
      updateOverlayStatus(gridApi);
    }

    if (shouldRefreshGridData) {
      refreshGridData(hardRefresh);
    }
  });

  const fromAddFilter = () => {
    const emptyFilterAdded = !last(vifFilters)?.arguments && filters.length < vifFilters.length;
    const emptyArguments = emptyFilterAdded || emptyFilterRemoved();

    return emptyArguments;
  };

  const emptyFilterRemoved = () => {
    for (let i = 0; i < filters.length; i++) {
      // if removing a filter column with an empty argument, then do not reload
      if (
        !filters[i].arguments &&
        !vifFilters.find((vFilter) => isEqual(vFilter.columns, (filters[i] as SoqlFilter).columns))
      ) {
        return true;
      }
    }
    return false;
  };

  // This function updates width of grouped columns when non-indented layout is used.
  // This is necessary because we do not store the actual width of the grouped columns in columnDefs.
  const updateGroupedColumnsWidth = (api: GridApi) => {
    const agGroupedColumns = filter(api.getColumnState(), (col) =>
      includes(col.colId, GROUP_COLUMN_PREFIX),
    ).map((col) => col.colId);
    const newWidths = compact(
      agGroupedColumns.map((colId) => {
        // Get the width from the columns
        const fieldName = colId.replace(`${GROUP_COLUMN_PREFIX}-`, '');
        const originalColumn = filter(columns, (col) => col.fieldName === fieldName)?.[0];
        const width = originalColumn?.width;
        const agGridColumn = api.getColumn(colId);
        if (agGridColumn && width && width !== agGridColumn.getActualWidth()) {
          return {
            key: colId,
            newWidth: width,
          };
        }
      }),
    );

    if (newWidths.length > 0) {
      api.setColumnWidths(newWidths);
    }
  };

  //#endregion useLayoutEffect

  //#region pivot useLayoutEffect

  //useLayoutEffect specifically for pivot column order and width changes
  useLayoutEffect(() => {
    const oldPivotOrder = agGridContext.current.hierarchyConfig?.pivotColumnOrder;
    const oldWidths = agGridContext.current.hierarchyConfig?.pivotColumnWidths;
    const newPivotOrder = hierarchyConfig.pivotColumnOrder;
    const newWidths = hierarchyConfig.pivotColumnWidths;

    const orderChanged = !isEqual(oldPivotOrder, newPivotOrder);
    const widthsChanged = !isEqual(oldWidths, newWidths);

    if (gridApi && pivotMode && (orderChanged || widthsChanged)) {
      if (orderChanged) {
        // Order changed - need full refresh + apply order/widths
        refreshGridData(true);

        setTimeout(() => {
          if (newPivotOrder && newPivotOrder.length > 0) {
            const currentColumnState = gridApi.getColumnState();
            const nonPivotColumns = currentColumnState.filter((col) => !col.colId?.includes(PIVOT_SEPARATOR));

            const orderedPivotColumns: ColumnState[] = [];
            newPivotOrder.forEach((pivotValue) => {
              const pivotColumns = currentColumnState.filter((col) =>
                col.colId?.startsWith(`${pivotValue}${PIVOT_SEPARATOR}`),
              );

              const columnsWithWidths = pivotColumns.map((col) => {
                const storedWidth = newWidths?.[col.colId];
                return {
                  ...col,
                  width: storedWidth || 150, // Use stored width or sensible default
                };
              });

              orderedPivotColumns.push(...columnsWithWidths);
            });

            gridApi.applyColumnState({
              state: [...nonPivotColumns, ...orderedPivotColumns],
              applyOrder: true,
            });
          }
        }, 0); // Delay to ensure grid is ready for state application
      } else if (widthsChanged && newWidths) {
        // Only widths changed - lightweight update
        const columnWidths = Object.entries(newWidths)
          .filter(([colId]) => colId.includes(PIVOT_SEPARATOR))
          .map(([colId, width]) => ({
            key: colId,
            newWidth: width as number,
          }));

        if (columnWidths.length > 0) {
          gridApi.setColumnWidths(columnWidths);
        }
      }
    }

    // Update context for next comparison
    agGridContext.current.hierarchyConfig = hierarchyConfig;
  }, [gridApi, hierarchyConfig, pivotMode, refreshGridData]);

  //#endregion pivot useLayoutEffect

  //#region Custom sort

  const [isCustomSortModalOpen, setIsCustomSortModalOpen] = useState<boolean>(false);
  const [selectedColumnsToCustomSort, setSelectedColumnsToCustomSort] = useState<ViewColumn[]>([]);
  const [distinctValuesCountMap, setDistinctValuesCountMap] = useState({});
  const [headerSortCycleIndex, setHeaderSortCycleIndex] = useState<number>(
    hierarchyConfig.headerSortCycleIndex ?? 0,
  );

  const gridRefWrapper = useRef<HTMLDivElement>(null);
  const gridRef = useRef<AgGridReact>(null);
  const ariaSortUpdateTimeout = useRef<NodeJS.Timeout>();

  const getSortIcons = useMemo(() => createGetSortIcons(currentSortState), [currentSortState]);

  useEffect(() => {
    if (isCustomSortEnabled()) {
      setHeaderSortCycleIndex(hierarchyConfig.headerSortCycleIndex ?? HEADER_SORT_INDEX.DEFAULT);
    }
  }, [vifOrderConfig]);

  const handleOrderConfigForRecompute = (handleOrderConfigForRecomputeProps: {
    updatedOrderConfig?: OrderConfig[];
  }) => {
    if (!isCustomSortEnabled()) {
      return;
    }
    const { updatedOrderConfig } = handleOrderConfigForRecomputeProps;
    let newOrderConfig: OrderConfig[] = vifOrderConfig;
    if (updatedOrderConfig) {
      newOrderConfig = updatedOrderConfig;
    }
    if (!isEqual(newOrderConfig, currentSortState)) {
      setCurrentSortState(newOrderConfig);
    }
    gridRef.current?.api?.refreshHeader();
  };

  useEffect(() => {
    handleOrderConfigForRecompute({});
  }, [currentSortState]);

  const renderUnindentedHeader = useRenderUnindentedHeader({
    gridApi,
    hierarchyColumns,
    isIndented,
    vifOrderConfig,
  });

  const renderIndentedHeader = useRenderIndentedHeader({
    gridApi,
    headerSortCycleIndex,
    hierarchyColumns,
    isIndented,
    sortSourceElement,
    vifOrderConfig,
  });

  useEffect(() => {
    if (!isCustomSortEnabled() || !gridApi || !gridRefWrapper.current) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      if (isIndented) {
        renderIndentedHeader({
          isIndented,
          headerSortCycleIndex,
          gridApi,
          gridRefWrapper,
          sortSourceElement,
          vifOrderConfig,
          hierarchyColumns,
        });
        refreshIndentedHeader({ gridApi, gridRefWrapper });
      } else {
        renderUnindentedHeader({
          isIndented,
          gridRefWrapper,
          gridApi,
          hierarchyColumns,
          vifOrderConfig,
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [gridApi, headerSortCycleIndex, hierarchyColumns, isIndented, vifOrderConfig]);

  useEffect(() => {
    eventBus.on('setOrderConfigForRecompute', handleOrderConfigForRecompute);
    return () => {
      eventBus.remove('setOrderConfigForRecompute', handleOrderConfigForRecompute);
      clearTimeout(ariaSortUpdateTimeout.current);
    };
  }, []);

  const groupedColumns = hierarchyColumns.filter((col) => col.isGrouping).map((col) => col.columnName);
  const handleNewColumnsLoaded = useCallback(() => {
    // We will use setTimeout and clearTimeout here to debounce the aria-sort value assigning.
    // This is because this function gets called multiple times when we sort a column.
    if (ariaSortUpdateTimeout.current) {
      clearTimeout(ariaSortUpdateTimeout.current);
    }
    ariaSortUpdateTimeout.current = setTimeout(() =>
      assignAriaSortToTableHeaders({
        gridRefWrapper,
        groupedColumns,
        headerSortCycleIndex,
        isIndented: Boolean(isIndented),
        sortState: currentSortState,
      }),
    );
  }, [currentSortState, groupedColumns, headerSortCycleIndex, isIndented]);

  useEffect(() => {
    if (!isCustomSortEnabled()) {
      return;
    }

    const gridElement = gridRefWrapper.current;
    if (!gridElement) {
      return;
    }

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (isSortMenuOrResizeClick(target)) {
        return;
      }

      const rowGroupColumns = gridRef.current?.api.getRowGroupColumns();
      const rowGroupColumnId = getRowGroupColumnForSort(target, rowGroupColumns)?.getColId();
      const isFirstHierarchyColumn =
        isEmpty(rowGroupColumns) || !rowGroupColumns
          ? false
          : rowGroupColumns[0].getColId() == rowGroupColumnId;
      const headerCell = target.closest('.ag-header-cell') as HTMLElement | null;
      if (!headerCell && !rowGroupColumnId) {
        return;
      }

      const isMultiSort = e.metaKey || e.ctrlKey;
      const sourceElement = rowGroupColumnId ? 'row-groups' : 'header';
      const currentHeaderSortCycle = HEADER_SORT_CYCLE[headerSortCycleIndex];
      const sortCycleTranslation = translateSortToHeaderSortCycle({ vifOrderConfig, rowGroupColumnId });
      let nextHierarchyIndex = null;
      // Check whether the headerSortIndex is correct by comparing the sort value from the order config with the header sort cycle index
      if (sourceElement === 'row-groups' && currentHeaderSortCycle != sortCycleTranslation) {
        nextHierarchyIndex = HEADER_SORT_CYCLE.indexOf(sortCycleTranslation);
        if (nextHierarchyIndex !== -1) {
          setHeaderSortCycleIndex(nextHierarchyIndex);
        }
      }
      setSortSourceElement(sourceElement);

      const colId = getColumnId(headerCell, isIndented, rowGroupColumnId, isFirstHierarchyColumn);
      if (!colId) {
        return;
      }

      if (colId === GROUP_COLUMN_PREFIX) {
        handleIndentedColumnSort({
          isMultiSort,
          headerSortCycleIndex: nextHierarchyIndex || headerSortCycleIndex,
          currentSortState,
          hierarchy: hierarchyConfig,
          hierarchyColumns,
          setHeaderSortCycleIndex,
          setCurrentSortState,
          handleCustomSortCycle,
          activeHierarchyId,
          handleIndentedHeaderSortIndex,
          sortSourceElement: sourceElement,
          rowGroupColumnId,
        });
        return;
      }

      handleRegularColumnSort({
        colId,
        isMultiSort,
        currentSortState,
        hierarchy: hierarchyConfig,
        hierarchyColumns,
        setHeaderSortCycleIndex,
        setCurrentSortState,
        handleCustomSortCycle,
        activeHierarchyId,
        handleIndentedHeaderSortIndex,
        renderUnindentedHeader,
        isIndentedConfigured: isIndented,
        gridRefWrapper,
        gridApi,
        headerSortCycleIndex: nextHierarchyIndex || headerSortCycleIndex,
        vifOrderConfig,
      });
    };

    gridElement.addEventListener('click', onClick);
    return () => {
      gridElement.removeEventListener('click', onClick);
    };
  }, [hierarchyColumns, currentSortState, gridRef, vifOrderConfig]);

  //#endregion Custom sort

  //#region icons

  const headerSortIconHTMLElement = (icon: React.ReactElement) => {
    const headerSortIcon = document.createElement('div');
    ReactDOM.render(
      <div className="ag-sort-indicator-icon ag-custom-header-icon">
        <ForgeIcon external external-type="standard" name={icon.props.name} />
        {renderSortIconTooltip()}
      </div>,
      headerSortIcon,
    );

    return headerSortIcon;
  };

  const columnsSidebarIconHTMLElement = () => {
    const columnsIcon = document.createElement('div');
    ReactDOM.render(<ForgeIcon name="view_column_outline" />, columnsIcon);

    return columnsIcon;
  };

  const headerMenuIconHTMLElement = () => {
    const headerMenuIcon = document.createElement('div');
    ReactDOM.render(
      <ForgeIcon className="agGrid-menu-icon" name="menu" data-testid="ag-header-menu" />,
      headerMenuIcon,
    );

    return headerMenuIcon;
  };

  const headerFilterIconHTMLElement = () => {
    const headerFilterIcon = document.createElement('div');
    ReactDOM.render(
      <ForgeIcon className="agGrid-menu-icon" name="filter_menu" data-testid="ag-filter-menu" />,
      headerFilterIcon,
    );

    return headerFilterIcon;
  };

  const iconsConfig = useMemo(() => {
    const config: { [key: string]: string | '() => HTMLDivElement' } = {
      columns: columnsSidebarIconHTMLElement.bind(this),
      sortAscending: headerSortIconHTMLElement.bind(
        this,
        <ForgeIcon external external-type="standard" name="arrow_upward" />,
      ),
      sortDescending: headerSortIconHTMLElement.bind(
        this,
        <ForgeIcon external external-type="standard" name="arrow_downward" />,
      ),
      sortUnSort: headerSortIconHTMLElement.bind(
        this,
        <ForgeIcon external external-type="standard" name="notes" />,
      ),
    };

    if (showAgGridColumnMenu) {
      config.menu = headerMenuIconHTMLElement.bind(this);
      config.filter = headerFilterIconHTMLElement.bind(this);
    }

    return config;
  }, [showAgGridColumnMenu]);

  //#endregion icons

  //#region AgTable callbacks

  const handleColumnReorder = useCallback(
    ({ api, finished }: ColumnMovedEvent) => {
      if (finished) {
        onColumnReorder(api.getColumnState());
      }
    },
    [onColumnReorder],
  );

  const setDistinctValuesCount = useCallback(async () => {
    if (isCustomSortEnabled()) {
      let distinctValuesCount;
      if (isEmpty(filterWhereClause)) {
        distinctValuesCount = await getDistinctValuesCountForColumns(datasetUid, columns);
      } else {
        distinctValuesCount = await getDistinctValuesCountForColumns(datasetUid, columns, filterWhereClause);
      }
      setDistinctValuesCountMap(distinctValuesCount[0]);
    }
  }, [columns, datasetUid, filterWhereClause]);

  useEffect(() => {
    if (gridApi && !isEqual(vifFilters, filters)) {
      setDistinctValuesCount();
    }
  }, [vifFilters]);

  const handleFirstDataRendered = useCallback(
    ({ api }: FirstDataRenderedEvent) => {
      updateColumnsSort({ api, vifOrderConfig, isIndented });
      if (isCustomSortEnabled()) {
        const raf = requestAnimationFrame(() => {
          if (isIndented) {
            renderIndentedHeader({
              isIndented,
              headerSortCycleIndex,
              gridApi,
              gridRefWrapper,
              sortSourceElement,
              vifOrderConfig: currentSortState,
              hierarchyColumns,
            });
            refreshIndentedHeader({ gridApi, gridRefWrapper });
          } else {
            renderUnindentedHeader({
              isIndented,
              gridRefWrapper,
              gridApi,
              hierarchyColumns,
              vifOrderConfig,
            });
          }
        });
        return () => cancelAnimationFrame(raf);
      }
    },
    [
      currentSortState,
      gridApi,
      gridRefWrapper,
      headerSortCycleIndex,
      hierarchyColumns,
      isIndented,
      renderIndentedHeader,
      renderUnindentedHeader,
      sortSourceElement,
      vifOrderConfig,
    ],
  );

  const handleColumnResize = useCallback(
    ({ columns: newColumns, finished }: ColumnResizedEvent) => {
      if (finished) {
        onColumnResize(newColumns);
      }
      if (!isCustomSortEnabled()) {
        return;
      }
      const raf = requestAnimationFrame(() => {
        if (isIndented) {
          renderIndentedHeader({
            isIndented,
            headerSortCycleIndex,
            gridApi,
            gridRefWrapper,
            sortSourceElement,
            vifOrderConfig: currentSortState,
            hierarchyColumns,
          });
          refreshIndentedHeader({ gridApi, gridRefWrapper });
        }
      });
      return () => cancelAnimationFrame(raf);
    },
    [
      currentSortState,
      gridApi,
      gridRefWrapper,
      headerSortCycleIndex,
      hierarchyColumns,
      isIndented,
      onColumnResize,
      renderIndentedHeader,
      sortSourceElement,
    ],
  );

  // TODO: grandTotalPivotData should be split out somehow,
  // so this doesn't reset any time hierarchyConfig changes.
  const resetGrandTotalData = useCallback(
    async (apiColumns: Column[] | null) => {
      const grandTotalRow = await getGrandTotalRow(hierarchyId);

      // If column is grouped, setting the total is handled in the autogroup attributes config
      const columnDefs = apiColumns?.map((column) => column.getColDef()) ?? [];
      const firstColumns = columnDefs.filter((column) => column?.rowGroupIndex != null);
      let firstColumn = firstColumns[0];
      if (!firstColumn) {
        firstColumn = columnDefs[0];
        const field = firstColumn?.field ?? '';
        if (
          showGrandTotal &&
          !get(grandTotalRow, [0, field]) &&
          hierarchyConfig.columnConfigurations?.filter((v) => v?.aggregation)?.length > 0
        ) {
          let totalLabel = I18n.t('shared.visualizations.charts.table.total');
          if (isTableAdvancedTotalingEnabled()) {
            const customTotalLabel = hierarchyConfig?.customLabels?.totals?.trim();
            totalLabel = !isBlank(customTotalLabel) ? customTotalLabel : totalLabel;
          }
          set(grandTotalRow, [0, field], totalLabel);
        }
      }
      if (!isEqual(grandTotalData, grandTotalRow)) {
        if (isPivotInHierarchy(hierarchyConfig)) {
          const grandTotalPivotData = constructPivotGrandTotal(grandTotalRow, hierarchyConfig);
          setGrandTotalData(grandTotalPivotData);
        } else {
          setGrandTotalData(grandTotalRow);
        }
      }
    },
    [getGrandTotalRow, grandTotalData, hierarchyConfig, hierarchyId, showGrandTotal],
  );

  const handleColumnVisibilityChange = useCallback(
    async (visChangedEvent: ColumnVisibleEvent) => {
      const { api, columns: eventColumns } = visChangedEvent;
      onColumnVisibilityChange(api.getColumnState(), eventColumns, hierarchyId);
      resetGrandTotalData(api?.getColumns());
    },
    [hierarchyId, onColumnVisibilityChange, resetGrandTotalData],
  );

  const handleColumnRowGroupChanged = useCallback(
    (event: ColumnRowGroupChangedEvent) => {
      const { api } = event;

      if (!event.columns) {
        return;
      }
      onColumnRowGroupChange(api.getColumnState(), event.columns, hierarchyId);
    },
    [hierarchyId, onColumnRowGroupChange],
  );

  const handleColumnValueChanged = useCallback(
    (event: ColumnValueChangedEvent) => {
      if (!event.columns) {
        return;
      }
      onColumnValueChange(event.columns, hierarchyId);
    },
    [hierarchyId, onColumnValueChange],
  );

  const handleColumnPivotChanged = useCallback(
    (event: ColumnPivotChangedEvent) => {
      const { api } = event;

      if (!event.columns) {
        return;
      }
      onColumnPivotChange(api.getColumnState(), event.columns, hierarchyId);
    },
    [hierarchyId, onColumnPivotChange],
  );

  const handleColumnPivotModeChanged = useCallback(
    (event: ColumnPivotModeChangedEvent) => {
      const { api } = event;

      onColumnPivotModeChange(api.isPivotMode(), hierarchyId);
    },
    [hierarchyId, onColumnPivotModeChange],
  );

  const handleColumnSort = useCallback(
    ({ api }: SortChangedEvent) => {
      onColumnSort(api.getColumnState(), hierarchyId);
    },
    [hierarchyId, onColumnSort],
  );

  //#endregion AgTable callbacks

  //#region Assorted useEffects

  // Very basic grand total support
  useEffect(() => {
    if (gridApi) {
      resetGrandTotalData(gridApi.getColumns());
    }
  }, [hierarchyConfig]);

  // EN-76216: Multi-level column sorting
  // When switching from a multi-sort indented layout (ON) to a flat layout (OFF),
  // this will force the sort order indicators (numbers) in the headers to update.
  useEffect(() => {
    refreshGridData();

    if (gridApi) {
      gridApi.refreshHeader();
    }
  }, [isIndented]);

  // EN-47419: Expand all groups
  useEffect(() => {
    if (gridApi) {
      if (!agGridOpenNodeLevel) {
        gridApi?.collapseAll();
      } else {
        gridApi?.forEachNode(function (node) {
          if (node.group && !node.expanded && node.level < agGridOpenNodeLevel) {
            node.setExpanded(true);
          }
        });
      }
    }
  }, [agGridOpenNodeLevel, gridApi]);

  // https://github.com/socrata/platform-ui/pull/15568
  // Part of this gigantic awful PR, which included an AG Grid upgrade.
  // In other words, I have no idea what feature this actually supports.
  // The way this PR was built/approved/merged is embarrassing.
  useEffect(() => {
    refreshGridData();
  }, [activeHierarchyId, showSubTotal]);

  /**
   * TODO: EN-79197
   * YIKES. Do not change the way these values are assigned.
   * Column formatting (and maybe other things) rely on _reference assignment_ here.
   * Meaning, code that uses these values will run before this useDeepCompareEffect runs,
   * but the code still gets the correct, up-to-date value because of the way its assigned.
   * Both useRef and useEffect should not be written this way.
   */
  useDeepCompareEffect(() => {
    agGridContext.current.columnFormats = columnFormats;
    agGridContext.current.columnMetadata = columnMetadata;
    agGridContext.current.currentRowStripeStyle = currentRowStripeStyle;
    agGridContext.current.customPivotOrder = hierarchyConfig.pivotColumnOrder;
    agGridContext.current.groupedColumns = groupedColumns;
    agGridContext.current.headerFormat = headerFormat;
    agGridContext.current.hierarchyConfig = hierarchyConfig;
    agGridContext.current.linkedAction = linkedAction;
    agGridContext.current.newTableActions = newTableActions;
    agGridContext.current.nonStandardAggregations = nonStandardAggregations;
    agGridContext.current.processDiscoveryContext = processDiscoveryContext;
    agGridContext.current.showNetTotal = showNetTotal;
    agGridContext.current.showSubTotal = showSubTotal;

    gridApi?.refreshCells({ force: true });
    gridApi?.refreshHeader();
  }, [
    columnFormats,
    columnMetadata,
    currentRowStripeStyle,
    groupedColumns,
    headerFormat,
    hierarchyConfig,
    linkedAction,
    newTableActions,
    nonStandardAggregations,
    processDiscoveryContext,
    showNetTotal,
    showSubTotal,
  ]);

  //#endregion Assorted useEffects

  //#region primer

  // EN-62412: Primer uses AG-Grid internal filtering.
  // These are NOT soql filters, this just converts them to vif filters.
  // The route that makes this work is insane:
  // AgGrid's filter UI -> filterModel -> soql filters -> vif datasource.filters ->
  // entire table re-renders -> normal SSRM querying
  const handleFilterChange = useCallback(
    (filterChangedEvent: FilterChangedEvent) => {
      const filterModel = filterChangedEvent.api.getFilterModel();
      if (!isEqual(filterModel, agFilterModel)) {
        const f = agGridFilterToVifFilter(filterModel, filterChangedEvent.api, datasetUid);
        setFilters(f);
        onFilterChange(f, filterModel);
      }
    },
    [agFilterModel, datasetUid, onFilterChange],
  );

  // There's apparently no way to set the FilterModel directly on Grid Props, only the api.
  const initializeFilterModel = useCallback(
    (api: GridApi) => {
      if (agFilterModel) {
        const currentFilterModel = api.getFilterModel();
        if (!isEqual(currentFilterModel, agFilterModel)) {
          api.setFilterModel(agFilterModel);
        }
      }
    },
    [agFilterModel],
  );

  // EN-61961: Primer has custom keyword search.
  // This useEffect abuses the existing handleGridReady to aggressively re-render the table.
  // Or rather, I assume its just aggressively re-rendering, there's no way we should actually
  // be calling handleGridReady multiple times on the same grid instance.
  useEffect(() => {
    if (gridApi) {
      // We are creating a GridReadyEvent that is defined in events.d.ts found
      // in the ag-grid-community node_module. This extends AgGridEvent which
      // extends AgEvent which has a required attribute "type" of type string.
      // Since I am not using it at all and have no actual event to pass, I
      // added an arbitrary string.
      handleGridReady({ type: 'gridReady', api: gridApi, context: undefined });
    }
  }, [searchString]);

  // EN-67997: Primer tries to show an overlay when search finds no rows.
  // This re-hides the overlay every frame.
  const updateOverlayStatus = (api: GridApi): void => {
    if (api.getDisplayedRowCount() === 0) {
      // If no rows were returned tell AgGrid to display the no rows overlay.
      // It can't do this automatically when using the server-side model.
      // There are instances in which the no rows overlay is not showing.
      // Here’s a solution from ag-Grid's GitHub issues.
      // https://github.com/ag-grid/ag-grid/issues/3849#issuecomment-1136706407
      setTimeout(() => {
        api?.showNoRowsOverlay();
      });
    } else {
      // Similarly, AgGrid can't hide the overlay itself if there is something to show.
      api.hideOverlay();
    }
  };

  const handleModelUpdated = useCallback((params: ModelUpdatedEvent) => {
    const api = params.api;
    updateOverlayStatus(api);
  }, []);

  const noRowsOverlay = useMemo(() => {
    return function noRowsOverlayWithProps() {
      return (
        <NoRowsAgGridOverlay
          imageClassName="agGrid-no-rows-overlay-icon"
          textSectionClassName="agGrid-no-rows-overlay-text"
        />
      );
    };
  }, []);

  // EN-63063: Primer shows column metadata description field in the column menu.
  const getMainMenuItems = useCallback(
    (menuItemsParams: GetMainMenuItemsParams): MenuItemDef[] => {
      const columnId = menuItemsParams.column.getColId();
      const metadataForColumn = columnMetadata.find(({ fieldName }) => fieldName === columnId);

      const descriptionContent = DOMPurify.sanitize(
        metadataForColumn?.description ||
          I18n.t('shared.visualizations.charts.table.column_menu.no_description'),
        { ALLOWED_TAGS: [] },
      );

      // Yes, this is gross. AgGrid's devs didn't expect anyone to want to put their own custom elements
      // in the dropdown menu, so we have to sneak 'em in via a menu option's name prop.
      return [
        {
          name: `<b class="column-description-title">${I18n.t(
            'shared.visualizations.charts.table.column_menu.description_label',
          )}</b> ${descriptionContent}`,
        },
      ];
    },
    [columnMetadata],
  );

  // EN-69614: Primer enables right-click -> copy, and this formats the cell for the clipboard.
  const processCellForClipboard = useCallback(
    (params: ProcessCellForExportParams) => {
      const colDef: ColDef = params.column.getColDef();

      const extractAnyAnchorText = (inputString: string) => {
        const anchorRegex = /<a[^>]*>(.*?)<\/a>/g;
        const result: string[] = [];
        let match: RegExpExecArray | null;

        while ((match = anchorRegex.exec(inputString)) !== null) {
          result.push(match[1]);
        }

        // if there is no match we just return the inputString
        return result[0] || inputString;
      };

      // this is similar to how ValueRenderer works
      const extendedParams: ICellRendererParams = { ...params, ...colDef.cellRendererParams, colDef };

      let formattedValue = agGridDataFormatter(
        colDef.cellRendererParams.domain,
        colDef.cellRendererParams.datasetUid,
        columnMetadata,
        nonStandardAggregations,
        extendedParams,
        columnFormats,
      );

      // URLs are formatted as anchor elements, so extracting out the visible string with this function if present
      formattedValue = extractAnyAnchorText(formattedValue);

      return formattedValue || params.value;
    },
    [columnMetadata, nonStandardAggregations, columnFormats],
  );

  //#endregion primer

  //#region a11y keyboard tooltips

  // If a user hovers over a cell/header, we show a tooltip... but not if they keyboard-navigate to it.
  // This fakes a mouseenter event when a cell/header is focused via keyboard, so that tooltips still show.
  const doFakeKeyboardFocusEvent = useCallback(
    (params: CellFocusedEvent | HeaderFocusedEvent, targetClass: string) => {
      if (!params.api || !params.column) {
        return;
      }
      const focusedTarget = document.querySelector(targetClass);
      if (focusedTarget) {
        const rect = focusedTarget.getBoundingClientRect();

        // pretend to move the mouse to the focused cell/header to trigger any tooltips
        focusedTarget.dispatchEvent(
          new MouseEvent('mouseenter', {
            bubbles: true,
            cancelable: true,
            clientX: rect.right,
            clientY: rect.bottom,
          }),
        );
      }
    },
    [],
  );

  const onCellFocusedCallback = useCallback(
    (params: CellFocusedEvent) => {
      doFakeKeyboardFocusEvent(params, '.ag-cell-focus');
    },
    [doFakeKeyboardFocusEvent],
  );

  const onHeaderFocusedCallback = useCallback(
    (params: HeaderFocusedEvent) => {
      doFakeKeyboardFocusEvent(params, '.ag-header-active');
    },
    [doFakeKeyboardFocusEvent],
  );

  //#endregion a11y keyboard tooltips

  //#region assorted props
  // @isaiahshiner has not audited all of these, but most of them seem benign.

  const getRowStyle = useCallback(
    (params: RowClassParams) => {
      return getAgTableRowStyle(params, columnMetadata, currentRowStripeStyle);
    },
    [columnMetadata, currentRowStripeStyle],
  );

  const overriddenDefaultColDef: ColDef = defaultColDefOverrides
    ? merge(defaultColDef, defaultColDefOverrides)
    : defaultColDef;

  const mobileView = !isLargeDesktop();

  const paginationPageSizeSelector = useMemo<number[] | boolean>(() => {
    return [10, 50];
  }, []);

  const columnMenuType = showColumnMenuKebab ? undefined : 'legacy';
  const suppressMenuHideValue = showColumnMenuKebab ? true : mobileView;

  const displayContextMenu = (params: GetContextMenuItemsParams) => {
    const defaultItems = params.defaultItems || [];
    return defaultItems.filter((item: string) => !['export', 'separator'].includes(item));
  };

  const isServerSideGroupOpenByDefaultCallback = useCallback(
    (params: IsServerSideGroupOpenByDefaultParams) =>
      params?.rowNode?.level < agGridOpenNodeLevel || isServerSideGroupOpenByDefault(params),
    [agGridOpenNodeLevel, isServerSideGroupOpenByDefault],
  );

  const getRowIdCallback = useCallback(() => {
    return uuid();
  }, []);

  const getLocaleTextCallback = useCallback(
    (key: GetLocaleTextParams) => {
      if (key.key == 'group' && isIndented) {
        const rowGroups = key.api.getRowGroupColumns();
        return rowGroups[0].getColDef().headerName;
      }

      if (key.variableValues) {
        return I18n.t(key.key, { scope: 'common.ag_grid_react', variable: key.variableValues });
      }

      return I18n.t(key.key, { scope: 'common.ag_grid_react' });
    },
    [isIndented],
  );

  useGridEscapeKeyHandler(gridApi, gridRefWrapper, showAgGridColumnMenu);

  //Create a ReportMetadata object for passing to the PdfPreviewModal
  //TODO: This pulls from the visualization. We should eventually pull this up to the report level
  const reportMetadata: ReportMetadata = {
    title: title || '',
    description: description || '',
    //Use the current time, formatted for a human, for the generated date
    generatedDate: moment().format(TIME_FORMATS.date_time_24h),
    reportUrl: window.location.href,
    //TODO: Pull from the report. Putting placeholders here for now.
    author: get(getCurrentUser(), 'displayName', ''),
    environment: window.location.host,
    status: 'final',
    filters: vifFilters,
    vifParameterOverrides,
  };

  //#endregion assorted props

  //#region final prop declarations

  const handleGridReady = useCallback(
    async ({ api }: GridReadyEvent) => {
      setGridApi(api);
      api.setGridOption('serverSideDatasource', datasource);
      initializeFilterModel(api);
      setDistinctValuesCount();
      resetGrandTotalData(api.getColumns());
    },
    [datasource, initializeFilterModel, setDistinctValuesCount, resetGrandTotalData],
  );

  const agGridModules = useMemo(() => {
    const modules = [
      ClipboardModule,
      ColumnsToolPanelModule,
      ExcelExportModule,
      MultiFilterModule,
      RangeSelectionModule,
      RowGroupingModule,
      ServerSideRowModelModule,
      SetFilterModule,
      SideBarModule,
    ];

    if (showAgGridColumnMenu) {
      modules.push(MenuModule);
    }

    return modules;
  }, [showAgGridColumnMenu]);

  const sidebarConfiguration: SideBarDef = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: !showAgGridColumnAggregations,
            suppressValues: !showAgGridColumnAggregations,
            suppressPivots: !isPivotFeatureFlagOn(), // Adds Column Labels section for pivots
            suppressPivotMode: !isPivotFeatureFlagOn() || !showAgGridColumnAggregations, // enables the pivot toggle button at the top of the sidebar only if column aggregations are also visible
            suppressColumnExpandAll: true,
            suppressColumnMove: false,
          },
        },
      ],
      // Set this value to the `id` of a panel you want opened by default.
      // Comment this out, or set `hiddenByDefault: true` to hide the tool panel by default.
      defaultToolPanel: (openToolPanelByDefault && 'columns') || undefined,
    }),
    [openToolPanelByDefault, showAgGridColumnAggregations],
  );

  const rawAgColumns = useColDefs({
    actionColumnDef,
    columnFormats,
    columnMetadata,
    columns,
    datasetUid,
    displayColumnFilters,
    distinctValuesCountMap,
    domain,
    getSortIcons,
    getUniqueValues,
    headerFormat,
    handleCustomSortCycle,
    hierarchyColumns,
    hierarchyConfig,
    nonStandardAggregations,
    removedHierarchyColumnNames,
    selectedColumns: selectedColumnsToCustomSort,
    setCurrentSortState,
    setIsCustomSortModalOpen,
    setSelectedColumns: setSelectedColumnsToCustomSort,
    showAgGridColumnAggregations,
    showAgGridColumnMenu,
    showColumnMenuKebab,
    useSetFilters,
    vifOrderConfig,
  });

  // Fixes an awful edge case bug. See EN-81488
  const agColumns = usePivotModeBugFixHack(rawAgColumns, pivotMode);

  const autoGroupAttributes = useAutoGroupAttributes({
    columnFormats,
    columns,
    datasetUid,
    distinctValuesCountMap,
    domain,
    getUniqueValues,
    handleCustomSortCycle,
    handleIndentedHeaderSortIndex,
    headerFormat,
    hierarchies: hierarchyColumns,
    hierarchyConfig,
    isIndented,
    selectedColumns: selectedColumnsToCustomSort,
    setCurrentSortState,
    setIsCustomSortModalOpen,
    setSelectedColumns: setSelectedColumnsToCustomSort,
    showColumnMenuKebab,
    singleAutoColumnWidth: hierarchyConfig.singleAutoColumnWidth,
    vifOrderConfig,
  });

  const agGridReactAttributes: AgGridReactProps = useMemo(
    () => ({
      autoGroupColumnDef: autoGroupAttributes,
      columnDefs: agColumns,
      columnMenu: columnMenuType,
      components: {
        customTooltip: SocrataTooltip,
      },
      context: agGridContext.current,
      defaultColDef: overriddenDefaultColDef,
      enableRangeSelection: true,
      getLocaleText: getLocaleTextCallback,
      getMainMenuItems: showAgGridColumnMenu ? getMainMenuItems : undefined,
      getRowId: getRowIdCallback,
      getRowStyle: getRowStyle,
      groupDisplayType: isIndented ? 'singleColumn' : 'multipleColumns',
      groupTotalRow: showSubTotal ? 'bottom' : undefined,
      headerHeight: 48,
      icons: iconsConfig,
      isServerSideGroupOpenByDefault: isServerSideGroupOpenByDefaultCallback,
      modules: agGridModules,
      multiSortKey: 'ctrl',
      noRowsOverlayComponent: noRowsOverlay,
      onCellFocused: onCellFocusedCallback,
      onColumnMoved: handleColumnReorder,
      onColumnPivotChanged: handleColumnPivotChanged,
      onColumnPivotModeChanged: handleColumnPivotModeChanged,
      onColumnResized: handleColumnResize,
      onColumnRowGroupChanged: handleColumnRowGroupChanged,
      onColumnValueChanged: handleColumnValueChanged,
      onColumnVisible: handleColumnVisibilityChange,
      onFilterChanged: handleFilterChange,
      onFirstDataRendered: handleFirstDataRendered,
      onGridReady: handleGridReady,
      onHeaderFocused: onHeaderFocusedCallback,
      onModelUpdated: handleModelUpdated,
      onNewColumnsLoaded: isCustomSortEnabled() ? handleNewColumnsLoaded : undefined,
      onRowGroupOpened: onRowGroupOpened,
      onSortChanged: isCustomSortEnabled() ? undefined : handleColumnSort, // TODO: remove when custom sort flag is enabled
      pagination: true,
      paginationAutoPageSize: !paginationPageSize,
      paginationPageSize: paginationPageSize,
      paginationPageSizeSelector: paginationPageSizeSelector,
      pinnedBottomRowData: showGrandTotal ? grandTotalData : undefined,
      pivotMode, // Setting for Pivot Mode toggle
      processCellForClipboard: processCellForClipboard,
      rowBuffer: 20,
      rowModelType: 'serverSide',
      rowSelection: 'multiple',
      serverSideDatasource: datasource,
      serverSidePivotResultFieldSeparator: PIVOT_SEPARATOR,
      sideBar: sidebarConfiguration,
      suppressAggFuncInHeader: true,
      suppressCsvExport: true, // we use custom export, so don't use ag-grids version
      suppressCutToClipboard: true, // we don't currently use these so hide them
      suppressDragLeaveHidesColumns: true,
      suppressMenuHide: suppressMenuHideValue,
      suppressRowHoverHighlight: true,
      tooltipMouseTrack: true,
      tooltipShowDelay: 0,
      ...(showAgGridColumnMenu && { getContextMenuItems: displayContextMenu }),
    }),
    [
      agColumns,
      agGridContext,
      agGridModules,
      autoGroupAttributes,
      columnMenuType,
      datasource,
      overriddenDefaultColDef,
      getLocaleTextCallback,
      getMainMenuItems,
      getRowIdCallback,
      getRowStyle,
      grandTotalData,
      handleColumnPivotChanged,
      handleColumnPivotModeChanged,
      handleColumnReorder,
      handleColumnResize,
      handleColumnRowGroupChanged,
      handleColumnSort,
      handleColumnValueChanged,
      handleColumnVisibilityChange,
      handleFirstDataRendered,
      handleGridReady,
      handleModelUpdated,
      handleNewColumnsLoaded,
      iconsConfig,
      isIndented,
      isServerSideGroupOpenByDefaultCallback,
      noRowsOverlay,
      handleFilterChange,
      onCellFocusedCallback,
      onHeaderFocusedCallback,
      onRowGroupOpened,
      paginationPageSize,
      paginationPageSizeSelector,
      pivotMode,
      processCellForClipboard,
      showAgGridColumnMenu,
      showGrandTotal,
      showSubTotal,
      sidebarConfiguration,
      suppressMenuHideValue,
    ],
  );

  const exportProps: IExportGridParams = useMemo(() => {
    return {
      activeHierarchyId,
      agGridContext,
      agGridReactAttributes,
      columns,
      datasetName,
      datasetUid,
      domain,
      getExportData,
      gridApi,
      headerFormat,
      hierarchyColumns,
      hierarchyConfig,
      removedHierarchyColumnNames,
      vifColumns,
      vifOrderConfig: currentSortState,
      vizUid,
    };
  }, [
    activeHierarchyId,
    agGridContext,
    agGridReactAttributes,
    columns,
    currentSortState,
    datasetName,
    datasetUid,
    domain,
    getExportData,
    gridApi,
    headerFormat,
    hierarchyColumns,
    hierarchyConfig,
    removedHierarchyColumnNames,
    vifColumns,
    vizUid,
  ]);

  //#endregion final prop declarations

  return (
    <>
      <div ref={gridRefWrapper} style={{ height: '100%', width: '100%' }}>
        <AgGridReact {...agGridReactAttributes} ref={gridRef} />
      </div>
      <ExportGrid {...exportProps} />
      {isPrintModalOpen && (
        <PdfPreviewModal
          linkedAction={linkedAction}
          processDiscoveryContext={processDiscoveryContext}
          dataset={{ domain, datasetUid }}
          getExportData={getExportData}
          gridApi={gridApi}
          showSubTotal={showSubTotal}
          showTotal={!isEmpty(grandTotalData)}
          onClose={onPrintModalClose}
          columnFormats={columnFormats}
          nonStandardAggregations={nonStandardAggregations}
          reportMetadata={reportMetadata}
          vifOrderConfig={vifOrderConfig}
          columnSortConfig={hierarchyConfig.columnSortConfig}
        />
      )}
    </>
  );
};

export const Grid = memo(GridNotMemoed, (prevProps, nextProps) => {
  // using expanded form because I set breakpoints here a lot
  const shouldSkipRender = isEqual(prevProps, nextProps);
  return shouldSkipRender;
});
